// Must match $show-delay and $show-duration in LoadingScreen.module.scss.
export const SHOW_DELAY_MS = 500;
export const SHOW_DURATION_MS = 180;
export const SHOW_DELAY_PROPERTY = '--loading-screen-show-delay';
// Must match $failsafe-delay in LoadingScreen.module.scss.
export const FAILSAFE_DELAY_MS = 15000;
export const LOADING_SCREEN_SELECTOR = '[data-testid="first-load-screen"]';

const now = () => (typeof performance === 'undefined' ? Date.now() : performance.now());

// Whether a cover's CSS failsafe animation has started revealing the page: its delay has
// elapsed, or it has already finished (however short reduced motion makes it).
export const hasFailsafeAnimationStarted = (failsafe) =>
  failsafe?.playState === 'finished' ||
  (typeof failsafe?.currentTime === 'number' && failsafe.currentTime >= FAILSAFE_DELAY_MS);

// Whether a server-rendered cover's CSS failsafe (`failsafe`, its animation) has started
// revealing the page. Without getAnimations, or while the animation isn't listed, its
// progress is unknown; the cover is styled no earlier than navigation start, so it can't
// have started before FAILSAFE_DELAY_MS, and from then on it is taken to have, so the page
// is never covered again. Only for server-rendered covers: one script mounts later starts
// its own clock. The cover's inline script (see coverInert.js) must decide the same way.
export const hasServerFailsafeStarted = (failsafe) =>
  failsafe ? hasFailsafeAnimationStarted(failsafe) : now() >= FAILSAFE_DELAY_MS;

// When an animation started, on the performance.now() clock. A pending one (created this
// frame or, before first paint, a few frames ago) has no start time yet; Chrome backdates
// it to when it was created, which is no later than the current frame's time.
export const getAnimationStart = (animation) => {
  if (typeof animation?.startTime === 'number') return animation.startTime;
  if (animation?.pending && typeof document.timeline?.currentTime === 'number') return document.timeline.currentTime;
  return typeof animation?.currentTime === 'number' ? now() - animation.currentTime : null;
};

// The loader's CSS show delay counts from when the cover is first styled, which on a
// server-rendered page can be well after navigation start. This is the delay that makes it
// appear SHOW_DELAY_MS after navigation start instead. When that has already passed, the
// loader is fully in from the cover's first paint rather than fading in.
export const getAlignedShowDelay = (coverStartedAt) => {
  const delayMs = Math.round(SHOW_DELAY_MS - coverStartedAt);
  return delayMs < 0 ? -SHOW_DURATION_MS : delayMs;
};

// Applies the aligned delay to <html> (the cover inherits it) unless the <head> script
// already did, and returns when the cover appears on the performance.now() clock, or null
// when that can't be told. `failsafe` is the cover's failsafe animation, which starts when
// the cover is first styled. While it is pending its exact start is unknown, so the delay
// is only applied once it is known to be late; until then the CSS delay applies from the
// cover's first style, which never shows the loader early.
export const alignShowDelay = (root, failsafe) => {
  const startedAt = getAnimationStart(failsafe);
  if (startedAt === null) return null;

  const appliedMs = parseFloat(root.style.getPropertyValue(SHOW_DELAY_PROPERTY));
  if (!Number.isNaN(appliedMs)) return startedAt + Math.max(0, appliedMs);

  const delayMs = getAlignedShowDelay(startedAt);
  if (failsafe.pending && delayMs >= 0) return startedAt + SHOW_DELAY_MS;
  root.style.setProperty(SHOW_DELAY_PROPERTY, `${delayMs}ms`);
  return startedAt + Math.max(0, delayMs);
};

// Inline <head> script for server-rendered covers: on the first frame the cover's start
// is known (before it is painted), times its show delay from navigation start as
// alignShowDelay does, so the loader appears on time even when the app's script is slow to
// arrive. Without script (or getAnimations) the CSS delay applies from first style.
export const buildShowDelayScript = () => `(function(){try{
var root=document.documentElement;
function align(){
var cover=document.querySelector(${JSON.stringify(LOADING_SCREEN_SELECTOR)});
if(!cover){if(document.readyState==='loading')requestAnimationFrame(align);return;}
if(root.style.getPropertyValue(${JSON.stringify(SHOW_DELAY_PROPERTY)})||!cover.getAnimations)return;
var animations=cover.getAnimations();
var failsafe=animations[animations.length-1];
if(!failsafe)return;
var resolved=typeof failsafe.startTime==='number';
var startedAt=resolved?failsafe.startTime:failsafe.pending?document.timeline.currentTime:null;
if(typeof startedAt!=='number')return;
var delay=Math.round(${SHOW_DELAY_MS}-startedAt);
if(!resolved&&delay>=0){requestAnimationFrame(align);return;}
root.style.setProperty(${JSON.stringify(SHOW_DELAY_PROPERTY)},(delay<0?${-SHOW_DURATION_MS}:delay)+'ms');
}
requestAnimationFrame(align);
}catch(e){}})();`;
